import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Brain, LineChart, Calendar, AlertCircle, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import DemoForm from "@/components/forms/DemoForm";

const features = [
  {
    icon: Brain,
    title: "Machine Learning Avanzado",
    description: "Algoritmos que aprenden de tus datos y mejoran continuamente la precisión de las predicciones.",
  },
  {
    icon: LineChart,
    title: "Múltiples Horizontes",
    description: "Forecasts desde 7 días hasta 365 días, con diferentes niveles de detalle según tus necesidades.",
  },
  {
    icon: Calendar,
    title: "Detección de Patrones",
    description: "Identifica automáticamente patrones estacionales, semanales y eventos especiales.",
  },
  {
    icon: AlertCircle,
    title: "Alertas Inteligentes",
    description: "Recibe notificaciones cuando las predicciones detectan oportunidades o riesgos.",
  },
];

const faqs = [
  {
    question: "¿Qué es el forecasting hotelero?",
    answer: "El forecasting hotelero es el proceso de predecir la demanda futura de habitaciones utilizando datos históricos, tendencias del mercado y otros factores. Permite a los hoteles tomar decisiones proactivas de pricing y distribución para maximizar ingresos."
  },
  {
    question: "¿Qué tan preciso es el forecasting de Zimoo?",
    answer: "Nuestro sistema alcanza una precisión superior al 95% en predicciones a corto plazo (7-14 días) y más del 85% a mediano plazo (30-90 días). La precisión mejora continuamente a medida que el sistema aprende de tus datos."
  },
  {
    question: "¿Qué datos necesita el sistema para hacer forecasting?",
    answer: "El sistema utiliza datos históricos de ocupación, tarifas, reservas, cancelaciones y no-shows. También incorpora datos externos como eventos locales, tendencias de búsqueda y precios de competencia."
  },
  {
    question: "¿Con qué frecuencia se actualizan las predicciones?",
    answer: "Los forecasts se actualizan diariamente de forma automática. Sin embargo, puedes generar actualizaciones en cualquier momento cuando hay cambios significativos en las condiciones del mercado."
  },
];

export default function ForecastingPage() {
  useEffect(() => {
    document.title = "Forecasting Hotelero | Predicción de Demanda con IA - Zimoo";
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    });
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="section-padding bg-gradient-to-b from-[#FAFAFA] to-white grain-overlay">
        <div className="max-w-7xl mx-auto container-padding relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <span className="label-text mb-4 block">FORECASTING HOTELERO</span>
            <h1 className="heading-1 mb-6">
              Predice la Demanda con{" "}
              <span className="text-gradient">Precisión</span>
            </h1>
            <p className="body-text text-lg mb-8">
              Anticípate al mercado con forecasting impulsado por inteligencia artificial. Toma decisiones proactivas, no reactivas.
            </p>
            <a href="#demo-forecasting">
              <Button className="btn-primary">
                Contáctanos
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* AEO Block */}
      <section className="py-12 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto container-padding text-center">
          <h2 className="text-xl font-semibold text-white mb-3">¿Qué es el forecasting en hotelería?</h2>
          <p className="text-gray-300 leading-relaxed">
            El forecasting hotelero es la práctica de predecir la demanda futura de habitaciones utilizando análisis de datos históricos, tendencias de mercado y modelos estadísticos. Permite a los hoteles anticipar ocupación, ajustar precios proactivamente y optimizar la distribución de inventario para maximizar ingresos.
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-16">
            <span className="label-text mb-4 block">CAPACIDADES</span>
            <h2 className="heading-2 mb-4">
              Forecasting de última generación
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="card-hover p-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1ABCB4] to-[#5200FF] flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-semibold text-[#0A0A0A] mb-2">{feature.title}</h3>
                <p className="text-[#404040] text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="section-padding bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="label-text mb-4 block">METODOLOGÍA</span>
              <h2 className="heading-2 mb-6">
                ¿Cómo funciona nuestro forecasting?
              </h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-[#0A0A0A] mb-2">1. Recopilación de Datos</h3>
                  <p className="text-[#404040]">
                    Integramos datos de tu PMS, channel manager, y fuentes externas como eventos, clima y competencia.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#0A0A0A] mb-2">2. Análisis de Patrones</h3>
                  <p className="text-[#404040]">
                    Nuestros algoritmos identifican patrones estacionales, tendencias semanales y correlaciones con eventos.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#0A0A0A] mb-2">3. Modelado Predictivo</h3>
                  <p className="text-[#404040]">
                    Machine learning genera predicciones para diferentes escenarios y horizontes temporales.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#0A0A0A] mb-2">4. Optimización Continua</h3>
                  <p className="text-[#404040]">
                    El sistema compara predicciones con resultados reales y ajusta modelos para mejorar precisión.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-8 border border-black/5 shadow-sm">
              <h3 className="font-semibold text-[#0A0A0A] mb-6">Métricas de Precisión</h3>
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-[#404040]">Corto plazo (7-14 días)</span>
                    <span className="font-bold text-[#1ABCB4]">95%+</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-[#1ABCB4] h-2 rounded-full" style={{ width: '95%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-[#404040]">Mediano plazo (30-90 días)</span>
                    <span className="font-bold text-[#1ABCB4]">88%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-[#1ABCB4] h-2 rounded-full" style={{ width: '88%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-[#404040]">Largo plazo (90-365 días)</span>
                    <span className="font-bold text-[#1ABCB4]">75%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-[#1ABCB4] h-2 rounded-full" style={{ width: '75%' }}></div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-[#404040] mt-4">
                La precisión mejora con el tiempo a medida que el sistema aprende de tus datos.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding bg-white">
        <div className="max-w-4xl mx-auto container-padding">
          <div className="text-center mb-12">
            <span className="label-text mb-4 block">BENEFICIOS</span>
            <h2 className="heading-2 mb-4">
              ¿Por qué es importante el forecasting?
            </h2>
          </div>

          <div className="space-y-6">
            {[
              {
                title: "Decisiones Proactivas",
                desc: "Anticípate a cambios en la demanda en lugar de reaccionar tarde. Ajusta precios y estrategias antes de que la competencia lo haga."
              },
              {
                title: "Mejor Gestión de Personal",
                desc: "Planifica turnos y contrataciones basándote en la ocupación esperada, optimizando costos operativos."
              },
              {
                title: "Optimización de Inventario",
                desc: "Decide cuántas habitaciones asignar a cada canal y segmento con base en predicciones de demanda."
              },
              {
                title: "Negociación con Socios",
                desc: "Usa forecasts para negociar mejores condiciones con mayoristas y agencias basándote en datos."
              },
            ].map((item, index) => (
              <div key={index} className="flex gap-4">
                <CheckCircle className="h-6 w-6 text-[#1ABCB4] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-[#0A0A0A] mb-1">{item.title}</h3>
                  <p className="text-[#404040]">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="section-padding bg-[#FAFAFA]">
        <div className="max-w-4xl mx-auto container-padding">
          <h2 className="heading-2 mb-8 text-center">Preguntas Frecuentes</h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left text-[#0A0A0A] font-medium">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#404040]">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section id="demo-forecasting" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="label-text mb-4 block">MEJORA TUS PREDICCIONES</span>
              <h2 className="heading-2 mb-6">
                Implementa Forecasting con IA
              </h2>
              <p className="body-text mb-6">
                Descubre cómo nuestro sistema de forecasting puede ayudarte a anticipar demanda y tomar mejores decisiones.
              </p>
              <Link to="/pricing-dinamico" className="inline-flex items-center text-[#1ABCB4] hover:underline font-medium">
                Aprende sobre Pricing Dinámico <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>
            <DemoForm sourcePage="forecasting-hotelero" />
          </div>
        </div>
      </section>
    </div>
  );
}
